import Vue from 'vue';
import ordersApi from '@/services/storeOrdersService';
import { Storage } from '@/services/Storage';

// --- Audio Notification State ---
const ringtone = new Audio('/assets/offline.wav');
ringtone.loop = true;
let isRinging = false;
let visibilityHandler = null;
// ------------------------------

const state = () => ({
  pendingOrders: [],
  orders: [],
  selectedOrder: null, // For order details modal
  loading: false,
  error: null,
  lastChecked: null,
  pollingTimerId: null,
  isFetchingPending: false,
  pendingCount: 0,
  isRegistered: false, // Track if store is registered for online orders
});

const getters = {
  getPendingOrders: (s) => s.pendingOrders,
  getOrdersList: (s) => s.orders,
  getSelectedOrder: (s) => s.selectedOrder,
  getPendingCount: (s) => s.pendingCount,
  getOrdersLoading: (s) => s.loading,
  getOrdersError: (s) => s.error,
  isOnlineStoreRegistered: (s) => s.isRegistered,
};

const mutations = {
  setPendingOrders(s, orders) {
    s.pendingOrders = orders || [];
    s.pendingCount = s.pendingOrders.length;
    s.lastChecked = new Date().toISOString();
  },
  setOrders(s, orders) {
    s.orders = orders || [];
  },
  setSelectedOrder(s, order) {
    s.selectedOrder = order;
  },
  setLoading(s, flag) {
    s.loading = !!flag;
  },
  setError(s, err) {
    s.error = err;
  },
  setPollingTimer(s, id) {
    s.pollingTimerId = id;
  },
  setPendingFetchInFlight(s, flag) {
    s.isFetchingPending = !!flag;
  },
  setRegistrationStatus(s, status) {
    s.isRegistered = !!status;
  },
};

const actions = {
  async checkRegistration({ commit }) {
    try {
      const store = await Storage.getDefaultStore();
      const storeDB = store?.StoreDB || store?.storeDB || store?.db || store?.slug;
      if (!storeDB) throw new Error('Default store not set');

      const resp = await ordersApi.checkRegistration(storeDB);
      const isRegistered = resp?.data === true;
      commit('setRegistrationStatus', isRegistered);
      return isRegistered;
    } catch (e) {
      console.error('checkRegistration failed', e);
      commit('setRegistrationStatus', false);
      return false;
    }
  },

  async fetchPending({ commit, state }) {
    if (state.isFetchingPending) {
      return;
    }

    try {
      commit('setPendingFetchInFlight', true);
      commit('setLoading', true);
      const store = await Storage.getDefaultStore();
      const storeDB = store?.StoreDB || store?.storeDB || store?.db || store?.slug;
      if (!storeDB) throw new Error('Default store not set');
      
      const resp = await ordersApi.getPending(storeDB);
      const list = Array.isArray(resp) ? resp : (resp?.data || []);
      commit('setPendingOrders', list);

      const newCount = state.pendingCount;

      if (newCount > 0 && !isRinging) {
        // Start ringing
        ringtone.play().catch(error => {
          // This might happen in strict browser environments, but should be fine in Electron
          console.warn("Audio notification playback failed to start:", error);
        });
        isRinging = true;
      } else if (newCount === 0 && isRinging) {
        // Stop ringing
        ringtone.pause();
        ringtone.currentTime = 0;
        isRinging = false;
      }

    } catch (e) {
      // Ensure sound stops if there's an error
      if (isRinging) {
        ringtone.pause();
        ringtone.currentTime = 0;
        isRinging = false;
      }
      console.error('fetchPending failed', e);
      commit('setError', e?.message || 'Failed to fetch pending orders');
    } finally {
      commit('setLoading', false);
      commit('setPendingFetchInFlight', false);
    }
  },

  async fetchOrders({ commit }, params = {}) {
    try {
      commit('setLoading', true);
      const store = await Storage.getDefaultStore();
      const storeDB = store?.StoreDB || store?.storeDB || store?.db || store?.slug;
      if (!storeDB) throw new Error('Default store not set');
      const resp = await ordersApi.getOrders(storeDB, params);
      const list = Array.isArray(resp) ? resp : (resp?.data || []);
      commit('setOrders', list);
      return resp;
    } catch (e) {
      console.error('fetchOrders failed', e);
      commit('setError', e?.message || 'Failed to fetch orders');
    } finally {
      commit('setLoading', false);
    }
  },

  async acceptOrder({ dispatch, rootGetters }, payload) {
    // DEPRECATED for LytStore online orders, which require the full order object.
    // This can still be used for other integrations if they only provide an orderId.
    const orderId = typeof payload === 'object' && payload !== null ? payload.orderId : payload;
    const saleNumberFromPayload = typeof payload === 'object' && payload !== null ? payload.saleNumber : undefined;
    const reason = typeof payload === 'object' && payload !== null ? payload.reason : undefined;
    const saleNumber = saleNumberFromPayload ?? rootGetters.getCurrentSale;

    const store = await Storage.getDefaultStore();
    const storeDB = store?.StoreDB || store?.storeDB || store?.db || store?.slug;
    await ordersApi.acceptOrder(storeDB, orderId, saleNumber, reason);
    await dispatch('fetchPending');
  },

  async acceptOnlineOrder({ dispatch }, order) {
    const store = await Storage.getDefaultStore();
    const storeDB = store?.StoreDB || store?.storeDB || store?.db || store?.slug;
    const result = await ordersApi.acceptLytStoreOrderAndCreateTable(storeDB, order);
    await dispatch('fetchPending');
    return result;
  },

  async rejectOrder({ dispatch }, { orderId, reason }) {
    const store = await Storage.getDefaultStore();
    const storeDB = store?.StoreDB || store?.storeDB || store?.db || store?.slug;
    await ordersApi.rejectOrder(storeDB, orderId, reason);
    await dispatch('fetchPending');
  },

  async adjustItems({ dispatch }, { orderId, payload }) {
    const store = await Storage.getDefaultStore();
    const storeDB = store?.StoreDB || store?.storeDB || store?.db || store?.slug;
    await ordersApi.adjustItems(storeDB, orderId, payload);
    await dispatch('fetchPending');
  },

  async updateOrderStatus({ dispatch }, { orderId, status, notes }) {
    const store = await Storage.getDefaultStore();
    const storeDB = store?.StoreDB || store?.storeDB || store?.db || store?.slug;
    await ordersApi.updateOrderStatus(storeDB, orderId, status, notes);
    await dispatch('fetchPending');
  },

  async fetchOrderDetails({ commit }, orderId) {
    try {
      commit('setLoading', true);
      const store = await Storage.getDefaultStore();
      const storeDB = store?.StoreDB || store?.storeDB || store?.db || store?.slug;
      if (!storeDB) throw new Error('Default store not set');
      const resp = await ordersApi.getOrder(storeDB, orderId);
      const orderData = (resp && !Array.isArray(resp) && resp.data !== undefined) ? resp.data : (resp || null);
      commit('setSelectedOrder', orderData);
      return orderData;
    } catch (e) {
      console.error('fetchOrderDetails failed', e);
      commit('setError', e?.message || 'Failed to fetch order details');
      return null;
    } finally {
      commit('setLoading', false);
    }
  },

  startPolling({ state, commit, dispatch }) {
    // clear existing
    if (state.pollingTimerId) {
      clearInterval(state.pollingTimerId);
      commit('setPollingTimer', null);
    }
    // immediate fetch
    dispatch('fetchPending');
    const id = setInterval(() => {
      if (!state.isFetchingPending) {
        dispatch('fetchPending');
      }
    }, 10000); // every 10 seconds
    commit('setPollingTimer', id);

    // visibility handling
    if (visibilityHandler) {
      document.removeEventListener('visibilitychange', visibilityHandler);
    }
    visibilityHandler = () => {
      if (document.hidden) return; // fetch on focus
      dispatch('fetchPending');
    };
    document.addEventListener('visibilitychange', visibilityHandler);
  },

  stopPolling({ state, commit }) {
    if (state.pollingTimerId) {
      clearInterval(state.pollingTimerId);
      commit('setPollingTimer', null);
    }
    if (visibilityHandler) {
      document.removeEventListener('visibilitychange', visibilityHandler);
      visibilityHandler = null;
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

